import type { CSSProperties, MouseEvent } from 'react';
import { memo, useEffect, useMemo, useState } from 'react';
import { CATEGORY_GROUPS } from '@listening/api-contract';
import { cn } from '@listening/utils/react-helpers';
import { useDebouncedCallback } from '@tanstack/react-pacer';
import { NavArrowLeft, NavArrowRight } from 'iconoir-react';
import { normalizeCanonicalUrl } from '@/seo/canonical';
import * as m from '@/paraglide/messages';

type PublicDocItem = {
  publicId: string;
  slug: string;
  title: string | null;
  category: string | null;
};

type PublicDocCategoryFacet = {
  category: string;
};

type DocumentListNavigation = {
  categoryFacets: ReadonlyArray<PublicDocCategoryFacet>;
  selectedCategory: string | undefined;
  currentPath: string;
  getCategoryHref: (category: string) => string;
  onCategoryChange: (category: string) => void;
};

type DocumentListSearch = {
  value: string;
  onChange: (value: string) => void;
};

type DocumentListPagination = {
  page: number;
  totalPages: number;
  getPageHref: (page: number) => string;
  onPageChange: (page: number) => void;
};

type DocumentListState = {
  loading: boolean;
  pending: boolean;
};

export type DocumentListModel = {
  docs: ReadonlyArray<PublicDocItem>;
  heading: string;
  navigation: DocumentListNavigation;
  search: DocumentListSearch;
  pagination: DocumentListPagination;
  state: DocumentListState;
};

export const DocumentList = ({ model }: { model: DocumentListModel }) => {
  const { navigation } = model;
  const docsByCategory = useMemo(
    () => groupDocsByCategory(model.docs, navigation.selectedCategory),
    [model.docs, navigation.selectedCategory],
  );
  const categoryEntries = useMemo(
    () => sortCategoryEntries(docsByCategory, navigation.selectedCategory),
    [docsByCategory, navigation.selectedCategory],
  );
  const categoryFacetsKey = useMemo(
    () => navigation.categoryFacets.map((facet) => facet.category).join('|'),
    [navigation.categoryFacets],
  );

  return (
    <div className='mx-auto grid max-w-6xl gap-8 px-4 lg:grid-cols-[240px_minmax(0,1fr)] lg:px-0'>
      <CategoryNavigation
        categoryFacets={navigation.categoryFacets}
        categoryFacetsKey={categoryFacetsKey}
        selectedCategory={navigation.selectedCategory}
        getCategoryHref={navigation.getCategoryHref}
        currentPath={navigation.currentPath}
        onCategoryChange={navigation.onCategoryChange}
      />

      <section className='min-w-0'>
        <DocumentListHeader heading={model.heading} search={model.search} />
        <DocumentListBody
          docs={model.docs}
          pagination={model.pagination}
          state={model.state}
          categoryEntries={categoryEntries}
        />
      </section>
    </div>
  );
};

function DocumentListHeader({
  heading,
  search,
}: {
  heading: string;
  search: DocumentListSearch;
}) {
  const [inputValue, setInputValue] = useState(search.value);
  const debouncedOnSearchChange = useDebouncedCallback(search.onChange, {
    wait: 300,
  });

  useEffect(() => {
    setInputValue(search.value);
  }, [search.value]);

  const handleInputChange = (value: string) => {
    setInputValue(value);
    debouncedOnSearchChange(value);
  };

  return (
    <div className='mb-8 flex flex-col gap-4 sm:flex-row sm:items-center'>
      <div className='min-w-0 flex-1'>
        <h2 className='text-2xl font-semibold tracking-[-0.04em] text-black'>
          {heading}
        </h2>
      </div>
      <input
        className='min-w-0 rounded-lg border border-gray-200 px-6 py-4 focus:ring-2 focus:ring-blue-500 focus:outline-none sm:w-80'
        onChange={(e) => handleInputChange(e.target.value)}
        placeholder={m.public_docs_search_placeholder()}
        type='text'
        value={inputValue}
      />
    </div>
  );
}

function DocumentListBody({
  docs,
  pagination,
  state,
  categoryEntries,
}: {
  docs: ReadonlyArray<PublicDocItem>;
  pagination: DocumentListPagination;
  state: DocumentListState;
  categoryEntries: ReadonlyArray<[string, Array<PublicDocItem>]>;
}) {
  const showEmpty = !state.loading && !state.pending && docs.length === 0;
  const showPagination = pagination.totalPages > 1;

  return (
    <>
      {showPagination && (
        <DocumentPagination
          page={pagination.page}
          totalPages={pagination.totalPages}
          getPageHref={pagination.getPageHref}
          onPageChange={pagination.onPageChange}
          className='mb-4'
        />
      )}

      <div aria-busy={state.loading} className='space-y-4'>
        {showEmpty ? (
          <p className='py-12 text-center text-neutral-665'>
            {m.public_docs_no_documents_found()}
          </p>
        ) : state.loading ? (
          <DocumentListSkeleton docs={docs} />
        ) : state.pending ? (
          <div className='min-h-64' />
        ) : (
          <DocumentGroups
            pagination={pagination}
            showPagination={showPagination}
            categoryEntries={categoryEntries}
          />
        )}
      </div>
    </>
  );
}

function DocumentGroups({
  pagination,
  showPagination,
  categoryEntries,
}: {
  pagination: DocumentListPagination;
  showPagination: boolean;
  categoryEntries: ReadonlyArray<[string, Array<PublicDocItem>]>;
}) {
  return (
    <div>
      {categoryEntries.map(([category, documents]) => (
        <div className='border-b border-neutral-280/40 py-5' key={category}>
          <h3 className='mb-3 text-sm font-semibold tracking-[-0.02em] text-black'>
            {category}
          </h3>
          <ul className='space-y-2.5'>
            {documents.map((doc) => (
              <li key={doc.publicId}>
                <a
                  href={normalizeCanonicalUrl(`/doc/${doc.slug}`)}
                  className='block text-sm leading-6 text-neutral-665 hover:text-blue-600 hover:underline'
                >
                  {doc.title}
                </a>
              </li>
            ))}
          </ul>
        </div>
      ))}

      {showPagination && (
        <DocumentPagination
          page={pagination.page}
          totalPages={pagination.totalPages}
          getPageHref={pagination.getPageHref}
          onPageChange={pagination.onPageChange}
          className='pt-6 pb-2'
        />
      )}
    </div>
  );
}

const CategoryNavigation = memo(function CategoryNavigation({
  categoryFacets,
  getCategoryHref,
  currentPath,
  onCategoryChange,
}: {
  categoryFacets: ReadonlyArray<PublicDocCategoryFacet>;
  categoryFacetsKey: string;
  selectedCategory: string | undefined;
  getCategoryHref: (category: string) => string;
  currentPath: string;
  onCategoryChange: (category: string) => void;
}) {
  return (
    <aside className='min-w-0 lg:sticky lg:top-4 lg:self-start'>
      <nav
        aria-label='Document categories'
        className='lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto lg:pr-2'
      >
        <ul className='space-y-1 border-l border-neutral-200'>
          {categoryFacets.map((facet) => {
            const category = facet.category;
            return (
              <CategoryNavigationItem
                category={category}
                getCategoryHref={getCategoryHref}
                currentPath={currentPath}
                key={category}
                onCategoryChange={onCategoryChange}
              />
            );
          })}
        </ul>
      </nav>
    </aside>
  );
}, areCategoryNavigationPropsEqual);

const CategoryNavigationItem = memo(function CategoryNavigationItem({
  category,
  getCategoryHref,
  currentPath,
  onCategoryChange,
}: {
  category: string;
  getCategoryHref: (category: string) => string;
  currentPath: string;
  onCategoryChange: (category: string) => void;
}) {
  const categoryHref = getCategoryHref(category);
  const normalizedCurrentPath = normalizePublicDocNavPath(currentPath);
  const categoryIsActive =
    normalizePublicDocNavPath(categoryHref) === normalizedCurrentPath;
  const handleCategoryClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (shouldUseNativeLink(event)) return;

    event.preventDefault();
    onCategoryChange(category);
  };

  return (
    <li className='public-doc-nav-item'>
      <div
        className={cn(
          'public-doc-nav-row',
          '-ml-px flex items-center border-l-2 transition-colors',
          'border-transparent hover:border-neutral-300 hover:bg-neutral-50',
        )}
      >
        <a
          className='public-doc-nav-link min-w-0 flex-1 truncate px-3 py-2 text-sm leading-5 text-neutral-665 transition-colors hover:text-black'
          aria-current={categoryIsActive ? 'page' : undefined}
          data-active={categoryIsActive ? '' : undefined}
          data-public-doc-nav-href={categoryHref}
          href={categoryHref}
          onClick={handleCategoryClick}
        >
          {category}
        </a>
      </div>
    </li>
  );
});

function shouldUseNativeLink(event: MouseEvent<HTMLAnchorElement>) {
  return (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.altKey ||
    event.ctrlKey ||
    event.shiftKey
  );
}

function normalizePublicDocNavPath(href: string) {
  try {
    const pathname = new URL(href, 'https://www.listening.com').pathname;
    return pathname === '/' ? pathname : pathname.replace(/\/$/, '');
  } catch {
    return href === '/' ? href : href.replace(/\/$/, '');
  }
}

function areCategoryNavigationPropsEqual(
  prev: {
    categoryFacetsKey: string;
    selectedCategory: string | undefined;
    getCategoryHref: (category: string) => string;
    currentPath: string;
    onCategoryChange: (category: string) => void;
  },
  next: {
    categoryFacetsKey: string;
    selectedCategory: string | undefined;
    getCategoryHref: (category: string) => string;
    currentPath: string;
    onCategoryChange: (category: string) => void;
  },
) {
  return (
    prev.categoryFacetsKey === next.categoryFacetsKey &&
    prev.selectedCategory === next.selectedCategory &&
    prev.getCategoryHref === next.getCategoryHref &&
    prev.currentPath === next.currentPath &&
    prev.onCategoryChange === next.onCategoryChange
  );
}

function getVisiblePages(page: number, totalPages: number) {
  const pages = new Set([1, totalPages]);
  for (
    let pageNumber = Math.max(1, page - 2);
    pageNumber <= Math.min(totalPages, page + 2);
    pageNumber += 1
  ) {
    pages.add(pageNumber);
  }

  const sortedPages = [...pages].toSorted((a, b) => a - b);
  return sortedPages.flatMap((pageNumber, index) => {
    const previousPage = sortedPages[index - 1];
    if (previousPage && pageNumber - previousPage > 1) {
      return [`ellipsis-${previousPage}-${pageNumber}`, pageNumber] as const;
    }
    return [pageNumber] as const;
  });
}

function DocumentPagination({
  page,
  totalPages,
  getPageHref,
  onPageChange,
  className,
}: {
  page: number;
  totalPages: number;
  getPageHref: (page: number) => string;
  onPageChange: (page: number) => void;
  className?: string;
}) {
  const visiblePages = getVisiblePages(page, totalPages);
  const previousPage = Math.max(1, page - 1);
  const nextPage = Math.min(totalPages, page + 1);
  const isFirstPage = page <= 1;
  const isLastPage = page >= totalPages;

  const stepLinkClass =
    'flex size-8 items-center justify-center rounded-md text-neutral-665 transition-colors';
  const handlePageClick =
    (nextPage: number, disabled = false) =>
    (event: MouseEvent<HTMLAnchorElement>) => {
      if (
        disabled ||
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.altKey ||
        event.ctrlKey ||
        event.shiftKey
      ) {
        return;
      }

      event.preventDefault();
      onPageChange(nextPage);
    };

  return (
    <nav
      aria-label='Document pagination'
      className={cn('flex items-center justify-start gap-1', className)}
    >
      <a
        aria-label='Previous page'
        aria-disabled={isFirstPage}
        className={cn(
          stepLinkClass,
          isFirstPage
            ? 'pointer-events-none opacity-30'
            : 'hover:bg-neutral-50 hover:text-black',
        )}
        href={isFirstPage ? undefined : getPageHref(previousPage)}
        onClick={handlePageClick(previousPage, isFirstPage)}
      >
        <NavArrowLeft className='size-4' />
      </a>

      <div className='flex items-center gap-0.5'>
        {visiblePages.map((pageItem) =>
          typeof pageItem === 'string' ? (
            <span
              aria-hidden='true'
              className='flex size-8 items-center justify-center text-sm text-neutral-280 select-none'
              key={pageItem}
            >
              &hellip;
            </span>
          ) : (
            <a
              aria-current={pageItem === page ? 'page' : undefined}
              className={cn(
                'flex size-8 items-center justify-center rounded-md text-sm tabular-nums transition-colors',
                pageItem === page
                  ? 'bg-black font-medium text-white'
                  : 'text-neutral-665 hover:bg-neutral-50 hover:text-black',
              )}
              href={getPageHref(pageItem)}
              onClick={handlePageClick(pageItem, pageItem === page)}
              key={pageItem}
            >
              {pageItem}
            </a>
          ),
        )}
      </div>

      <a
        aria-label='Next page'
        aria-disabled={isLastPage}
        className={cn(
          stepLinkClass,
          isLastPage
            ? 'pointer-events-none opacity-30'
            : 'hover:bg-neutral-50 hover:text-black',
        )}
        href={isLastPage ? undefined : getPageHref(nextPage)}
        onClick={handlePageClick(nextPage, isLastPage)}
      >
        <NavArrowRight className='size-4' />
      </a>
    </nav>
  );
}

function groupDocsByCategory(
  docs: ReadonlyArray<PublicDocItem>,
  selectedCategory: string | undefined,
) {
  return docs.reduce<Record<string, Array<PublicDocItem>>>((acc, doc) => {
    const category = selectedCategory ?? doc.category ?? 'Other';
    acc[category] ??= [];
    acc[category].push(doc);
    return acc;
  }, {});
}

function sortCategoryEntries(
  docsByCategory: Record<string, Array<PublicDocItem>>,
  selectedCategory: string | undefined,
) {
  if (selectedCategory) return Object.entries(docsByCategory);

  const order = new Map(
    Object.keys(CATEGORY_GROUPS).map((category, index) => [category, index]),
  );

  return Object.entries(docsByCategory).toSorted(([a], [b]) => {
    const aOrder = order.get(a) ?? Number.MAX_SAFE_INTEGER;
    const bOrder = order.get(b) ?? Number.MAX_SAFE_INTEGER;
    if (aOrder !== bOrder) return aOrder - bOrder;
    if (a === 'Other') return 1;
    if (b === 'Other') return -1;
    return a.localeCompare(b);
  });
}

function SkeletonLine({
  className,
  style,
}: {
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <div
      className={`animate-pulse rounded bg-gray-200 ${className ?? 'h-4 w-full'}`}
      style={style}
    />
  );
}

function getSkeletonWidth(text: string | null, min: number, max: number) {
  const charCount = text?.length ?? min;
  return `${Math.min(max, Math.max(min, charCount))}ch`;
}

export function DocumentListSkeleton({
  docs = [],
}: {
  docs?: ReadonlyArray<PublicDocItem>;
}) {
  const groupedDocs = groupDocsByCategory(docs, undefined);
  type SkeletonGroup = [
    string,
    Array<{ publicId: string; title: string | null }>,
  ];
  const skeletonGroups =
    docs.length > 0
      ? sortCategoryEntries(groupedDocs, undefined).map(
          ([category, documents]) =>
            [
              category,
              documents.map((doc) => ({
                publicId: doc.publicId,
                title: doc.title,
              })),
            ] satisfies SkeletonGroup,
        )
      : Array.from(
          { length: 4 },
          (_, index) =>
            [
              `category-${index.toString()}`,
              Array.from({ length: 3 }, (__, docIndex) => ({
                publicId: `doc-${index.toString()}-${docIndex.toString()}`,
                title: null,
              })),
            ] satisfies SkeletonGroup,
        );

  return (
    <div className='space-y-4'>
      {skeletonGroups.map(([category, documents]) => (
        <div className='border-b border-neutral-280/40 py-5' key={category}>
          <SkeletonLine
            className='mb-3 h-5'
            style={{ width: getSkeletonWidth(category, 12, 22) }}
          />
          <div className='space-y-2.5'>
            {documents.map((doc) => (
              <SkeletonLine
                className='h-4 max-w-full'
                key={doc.publicId}
                style={{ width: getSkeletonWidth(doc.title, 24, 92) }}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
